<script setup lang="ts">
import type { ReferencesListingData } from '~/types/api/ReferencesListing'

const props = defineProps<{
  reference: ReferencesListingData
}>()

const referenceLocaleLink = useLocaleLink({
  name: 'references-slug-id',
  params: {
    slug: props.reference.slug,
    id: props.reference.id,
  },
})

const imageData = getImageSrcSizesData(props.reference.image, {
  preload: true,
  defaultFormat: '620x1240',
  sizes: `
    (max-width: 767.99px) 95vw,
    (max-width: 1199.99px) 47vw,
    (max-width: 1399.99px) 500px,
    620px
  `,
  srcsetToGenerate: [
    ['620x1240', '620w'],
    ['1240x2480', '1240w'],
  ],
})
</script>

<template>
  <article class="c-references-thumb">
    <div class="c-references-thumb__image">
      <img
        :src="imageData.src"
        :srcset="imageData.srcset"
        :sizes="imageData.sizes"
        :alt="reference.name"
        loading="lazy"
      />
    </div>

    <h2 class="c-references-thumb__title">{{ reference.name }}</h2>

    <div
      class="c-references-thumb__description"
      v-html="reference.description"
    />

    <div class="c-references-thumb__link">
      <NuxtLink
        class="c-link c-link--with-leading-bar c-link--block-hover"
        :to="referenceLocaleLink"
        >{{ $t('pages.references.thumb.showDetail') }}</NuxtLink
      >
    </div>
  </article>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-references-thumb {
  --news-thumb--title--color: var(--color-primary);

  width: 100%;
  height: 100%;
  position: relative;
  min-width: 0;
  display: flex;
  flex-direction: column;

  @media (hover: hover) {
    &:hover {
      --news-thumb--title--color: var(--color-secondary);
    }
  }

  &__image {
    width: 100%;
    aspect-ratio: 620 / 480;
    flex: 0 0 auto;
    min-height: 0;
    overflow: hidden;
    margin-bottom: 22px;
    background-color: var(--color-solid-grey);

    @include breakpoint-down('sm') {
      margin-bottom: 16px;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__title {
    font-size: 40px;
    font-weight: 400;
    line-height: 1;
    margin-bottom: 40px;
    color: var(--news-thumb--title--color);
    transition: color var(--transition-duration)
      var(--transition-timing-function);

    @include breakpoint-down('md') {
      font-size: 32px;
      margin-bottom: 32px;
    }

    @include breakpoint-down('xs') {
      font-size: 28px;
      margin-bottom: 24px;
    }
  }

  &__description {
    margin-bottom: 40px;
    line-height: 1.5;

    & > :deep(*):last-child {
      margin-bottom: 0;
    }

    @include breakpoint-down('md') {
      font-size: 17px;
    }

    @include breakpoint-down('xs') {
      font-size: 15px;
      margin-bottom: 16px;
    }
  }

  &__link {
    margin-top: auto;
    font-weight: 600;

    @include breakpoint-down('xs') {
      font-size: 16px;
    }
  }
}
</style>
